import type { MethodGraph, MethodNode } from '../schemas/index.js';
import { lookupTerm, type NodeTypeIri } from '../vocabulary/index.js';

/** One outgoing edge, flattened out of the node's many-valued properties. */
export interface GraphReference {
  readonly from: string;
  readonly property: string;
  readonly to: string;
  /** Position within the property's value array; 0 for single-valued terms. */
  readonly position: number;
}

export interface GraphIndex {
  readonly nodes: readonly MethodNode[];
  /** First node wins for a repeated `@id`; MTH-002 reports the collision. */
  readonly byId: ReadonlyMap<string, MethodNode>;
  readonly duplicateIds: readonly string[];
  readonly byType: ReadonlyMap<string, readonly MethodNode[]>;
  readonly references: readonly GraphReference[];
  /** Incoming edges keyed by target id, then by property. */
  readonly incoming: ReadonlyMap<string, readonly GraphReference[]>;
}

function asStringValues(value: unknown): readonly string[] {
  if (typeof value === 'string') return [value];
  if (Array.isArray(value)) return value.filter((v): v is string => typeof v === 'string');
  return [];
}

/**
 * Builds the read model every rule shares. Reference extraction is driven by the
 * term table (`kind: 'id'`), never by a hand-maintained list of property names,
 * so a new reference term is walked the moment it is declared.
 */
export function indexGraph(graph: MethodGraph): GraphIndex {
  const nodes = graph['@graph'];
  const byId = new Map<string, MethodNode>();
  const duplicateIds: string[] = [];
  const byType = new Map<string, MethodNode[]>();
  const references: GraphReference[] = [];
  const incoming = new Map<string, GraphReference[]>();

  for (const node of nodes) {
    const id = node['@id'];
    if (byId.has(id)) duplicateIds.push(id);
    else byId.set(id, node);

    const bucket = byType.get(node['@type']);
    if (bucket === undefined) byType.set(node['@type'], [node]);
    else bucket.push(node);

    for (const [key, value] of Object.entries(node)) {
      if (key === '@id' || key === '@type') continue;
      const term = lookupTerm(key);
      if (term === undefined || term.kind !== 'id') continue;
      const targets = asStringValues(value);
      for (let position = 0; position < targets.length; position += 1) {
        const to = targets[position];
        if (to === undefined) continue;
        const reference: GraphReference = { from: id, property: term.term, to, position };
        references.push(reference);
        const inbound = incoming.get(to);
        if (inbound === undefined) incoming.set(to, [reference]);
        else inbound.push(reference);
      }
    }
  }

  return { nodes, byId, duplicateIds, byType, references, incoming };
}

export function nodesOfType(index: GraphIndex, ...types: readonly NodeTypeIri[]): readonly MethodNode[] {
  const result: MethodNode[] = [];
  for (const type of types) result.push(...(index.byType.get(type) ?? []));
  return result;
}

/** True when a string property is absent, empty or whitespace only. */
export function isBlank(value: string | undefined): boolean {
  return value === undefined || value.trim().length === 0;
}
